import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { weekStartsOn } from '@app/global-constants';
import { UserDatetimeService } from '@services/user-datetime.service';
import { CalendarEvent, CalendarView } from 'angular-calendar';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  isBefore,
  isEqual,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { CalendarDatesParams } from './calendar-dates-params.interface';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  constructor(
    @Inject(LOCALE_ID) readonly locale: string,
    private readonly userDatetimeService: UserDatetimeService,
  ) {}

  createCalendarEvents<T>(
    data: T[],
    eventStartKey: keyof T,
    eventEndKey: keyof T,
    nameKey?: keyof T,
    classTypeKey?: keyof T,
    isOnlineKey?: keyof T,
    isCancelledKey?: keyof T,
  ): CalendarEvent<T>[] {
    return data.map((el) => {
      const start = this.userDatetimeService.convertFromAPIDatetime(el[eventStartKey] as string | Date);
      const end = this.userDatetimeService.convertFromAPIDatetime(el[eventEndKey] as string | Date);
      const startRef = startOfDay(start);
      startRef.setSeconds(start.getSeconds());
      startRef.setMilliseconds(start.getMilliseconds());
      const endRef = endOfDay(end);
      endRef.setSeconds(end.getSeconds());
      endRef.setMilliseconds(end.getMilliseconds());
      const allDay = isSameDay(start, end) && isEqual(start, startRef) && isEqual(end, endRef);

      return {
        start,
        end,
        title: `${el[nameKey] as string | undefined}<div class="details">${
          el[isCancelledKey] ? $localize`(Odwołano) ` : ''
        }${(el[classTypeKey] as string | undefined) ?? ''}${el[isOnlineKey] ? $localize` (online)` : ''}</div>`,
        meta: el,
        allDay,
        cssClass: `${isBefore(end, new Date()) ? 'past' : el[isCancelledKey] ? 'cancelled' : ''} ${
          allDay ? 'all-day' : ''
        }`,
      };
    });
  }

  getDateParams(mode: CalendarView, date: Date): CalendarDatesParams {
    let params: CalendarDatesParams;
    switch (mode) {
      case 'month':
        params = { start: startOfMonth(date), end: endOfMonth(date) };
        break;
      case 'week':
        params = {
          start: startOfWeek(date, { locale: { code: this.locale }, weekStartsOn }),
          end: endOfWeek(date, { locale: { code: this.locale }, weekStartsOn }),
        };
        break;
      case 'day':
        params = { start: startOfDay(date), end: endOfDay(date) };
        break;
    }
    params.start = this.userDatetimeService.convertToAPIDatetime(params.start);
    params.end = this.userDatetimeService.convertToAPIDatetime(params.end);
    return params;
  }
}
